import React, { useEffect, useRef, useState } from "react";
import TransparentBlackBg from "../../widgets/transparent-black-background/transparent-black-background";
import { DialogHeaderBackButton } from "../Dialog/CommonDialogHeader";
import { DialogConfigLookup } from "../Dialog/DialogConfig";
import { DialogKind } from "../Dialog/DialogEntities";
import { Dispatch } from "../Dispatch";
import { UILayoutMode } from "../UILogicControl/UILogicControlEntities";
import { ContentURL, getContentUrl } from "../Utils/ContentURL";
import { BrandedImage, GetBrandedUrl } from "../Utils/BrandedContentUrls";
import { SetErrorMessages } from "../Utils/CommonHelpers";
import { LoadMyCards } from "../Payment/PaymentHelper";
import { Api } from "../../Services/Api";
import "./AddPaymentMethod.scss";
import "./CardRegistration.scss";
import { useAppState } from "../../Redux/ReduxHooks";
import { SdkLoadingState } from "../PayPal/PayPalState";
import { GetVerificationTokenFromMessage } from "./CabchargeVerificationToken";

/** Progress of adding the Cabcharge digital product to the user's account. */
type AddProductStatus = "Awaiting" | "Adding" | "Added" | "Failed";

/**
 * Dialog to show Cabcharge Authentication iFrame for adding Cabcharge Digital Product as a payment method.
 */
export const AddCabchargeDigitalProduct: React.FC = () => {

    const IsMobileDevice = useAppState(i => i.uiLogicControl.LayoutMode === UILayoutMode.Mobile);
    const iFrameLoadingState = useAppState(i => i.payment.CabchargeIFrameLoadingState);
    const iFrameUrl = useAppState(i => i.payment.CabchargeIFrameUrl);

    const [addProductStatus, setAddProductStatus] = useState<AddProductStatus>("Awaiting");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const hasSentTokenRef = useRef(false);

    function HideAddCabchargeDigitalProductDialog() {
        Dispatch.Payment.HideAddCabchargeDigitalProductDialog();
        Dispatch.Payment.IFrameLoadingState(SdkLoadingState.NotRequested);
    }

    /** Close the Cabcharge flow and return to the "Add Payment Method" list so the user can add another card. */
    function AddAnotherCard() {
        Dispatch.Payment.HideAddCabchargeDigitalProductDialog();
        Dispatch.Payment.IFrameLoadingState(SdkLoadingState.NotRequested);
        Dispatch.Payment.ShowAddPaymentMethodDialog();
    }

    /** Send the verification token (the `id` posted back by the iFrame on success) to MPS to add the selected Cabcharge digital product to the account. */
    async function AddCabchargeProductToAccount(verificationToken: string) {
        setAddProductStatus("Adding");

        const result = await Api.PaymentV2.AddCabchargeDigitalProduct({ VerificationToken: verificationToken });

        if (result.isSuccess) {
            // Close the iFrame by replacing it with the success message, and refresh the card list in the background so the new product appears in the wallet.
            setAddProductStatus("Added");
            LoadMyCards();
        } else {
            const error = SetErrorMessages(result);
            setErrorMessage(error.errorMessage);
            setAddProductStatus("Failed");
        }
    }

    /** Complete the verification once we have a valid token. Sent once only. */
    function CompleteVerification(verificationToken: string | null) {
        if (!verificationToken) return;
        if (hasSentTokenRef.current) return;
        hasSentTokenRef.current = true;

        Dispatch.Payment.RefreshVerificationToken(verificationToken);
        AddCabchargeProductToAccount(verificationToken);
    }

    useEffect(() => {
        // Only register for messages while the iFrame is visible.
        if (iFrameLoadingState !== SdkLoadingState.FinishedSuccess || iFrameUrl === null) return;

        const targetOrigin = new URL(iFrameUrl).origin;

        /** The iFrame posts the verification token (`id`) back to the parent once verification succeeds. */
        function ReceiveMessage(event: MessageEvent) {
            // Security check: only accept messages posted from the iFrame's origin.
            if (event.origin !== targetOrigin) return;
            CompleteVerification(GetVerificationTokenFromMessage(event.data));
        }

        window.addEventListener("message", ReceiveMessage, false);
        return () => window.removeEventListener("message", ReceiveMessage, false);
    }, [iFrameLoadingState, iFrameUrl]);

    return (
        <>
            <div className="cardRegistrationPanel cardRegistrationPanelDefaultLocation add-cabcharge-digital-product-panel">
                {
                    IsMobileDevice && <DialogHeaderBackButton Config={DialogConfigLookup(DialogKind.AddPaymentMethod)!} />
                }
                {
                    !IsMobileDevice && <img onClick={HideAddCabchargeDigitalProductDialog} className="cardRegistrationPanelClose" src={getContentUrl(ContentURL.images.buttons.greyX)} alt="Close icon" />
                }
                {addProductStatus !== "Added" && <div className="add-payment-method-title">Add Payment Method</div>}
                <div className="cabcharge-iframe-container">
                    {addProductStatus === "Added" && <div className="cardRegistrationSuccessFrame">
                        <div className="cardRegistrationSuccessHeaderMessage">Thanks, your card <br></br>has been added.</div>
                        <img className="PayIllustrationIcon" src={GetBrandedUrl(BrandedImage.AddPaymentCardIllustration)} alt="Image of a payment card" />
                        <button onClick={AddAnotherCard} className="AddAnotherCardButton">Add another card</button>
                        <button onClick={HideAddCabchargeDigitalProductDialog} className="CardRegistrationDoneButton">I'm good to go</button>
                    </div>}
                    {addProductStatus === "Failed" && <div className="cabcharge-digital-product-error">{errorMessage}</div>}
                    {addProductStatus === "Adding" && <img className="cabcharge-digital-product-loading" src={getContentUrl(ContentURL.images.Loading)} alt="Loading" />}
                    {addProductStatus === "Awaiting" && iFrameLoadingState === SdkLoadingState.InProgress && <img className="cabcharge-digital-product-loading" src={getContentUrl(ContentURL.images.Loading)} alt="Loading" />}
                    {addProductStatus === "Awaiting" && iFrameLoadingState === SdkLoadingState.FinishedError && <div className="cabcharge-digital-product-error">Sorry, something went wrong. Please try again later</div>}
                    {addProductStatus === "Awaiting" && iFrameLoadingState === SdkLoadingState.FinishedSuccess && iFrameUrl !== null && <div>
                        <iframe className="cabcharge-digital-product-frame" scrolling="no" title="Add Cabcharge Digital Product" src={iFrameUrl} />
                    </div>}
                </div>
            </div>
            <TransparentBlackBg onClick={() => { }} />
        </>

    );
}
